import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

import { UserContext } from '../../context/UserContext';
import { alCambiarSmack, alCambiarSmackPendientes, publicarSmackUnread } from '../../lib/smackBus';
import useSmackPolling from './useSmackPolling';
import ChannelList from './ChannelList';
import ChannelView from './ChannelView';
import CreateChannelModal from './CreateChannelModal';
import HelpVideoModal from './HelpVideoModal';
import PendingIssuesPanel from './PendingIssuesPanel';

const CHANNELS_POLL_MS = 15000;

/**
 * Smack: mini-chat equipo↔club (plan 21-ago). Desktop: dos paneles (lista de
 * canales + canal). Móvil: vistas apiladas — lista O canal con botón atrás.
 * Tiempo real v1 por polling (como el comandero); el canal abierto sondea a 5 s
 * dentro de ChannelView, y esta página refresca la lista a 15 s.
 */
export default function SmackPage() {
  const userCtx = useContext(UserContext);
  const [token, , , , , , , accessPrivileges, allVenues] = userCtx;
  // Posición 18 (candado ComanderoMenuSinRecargar): flag de Smack del venue.
  const venueSmackEnabled = Boolean(userCtx[18]);
  const isSuperadmin = accessPrivileges === 'superadmin';
  const smackVisible = venueSmackEnabled || isSuperadmin;

  const [channels, setChannels] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [myUserId, setMyUserId] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [vista, setVista] = useState('canales'); // 'canales' | 'pendientes'
  const [creandoCanal, setCreandoCanal] = useState(false);
  // SidebarBest ya sondea /v1/smack/issues para su propio badge — se lee ese
  // mismo total del bus en vez de abrir un segundo poller solo para la pestaña.
  const [pendientesCount, setPendientesCount] = useState(0);
  const [viendoVideo, setViendoVideo] = useState(false);
  useEffect(
    () =>
      alCambiarSmackPendientes((event) => {
        const total = Number(event?.detail?.total);
        if (!Number.isNaN(total)) setPendientesCount(total);
      }),
    []
  );

  // El contexto no expone el id del usuario (solo el email), y "es mío" se
  // decide contra author_id — una llamada a /me al abrir la página.
  useEffect(() => {
    if (!token) return;
    axios
      .get('/v1/users/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMyUserId(r.data?.id || null))
      .catch(() => {});
  }, [token]);

  const cargarCanales = useCallback(() => {
    if (!token || !smackVisible) return;
    axios
      .get('/v1/smack/channels', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const lista = Array.isArray(r.data) ? r.data : [];
        setChannels(lista);
        setError('');
        // La lista ya trae los no-leídos por canal: re-publicar el total deja
        // el badge del menú fresco a 15 s DENTRO de Smack sin peticiones extra
        // (fuera de Smack sigue el régimen de 60 s del sidebar).
        publicarSmackUnread(lista.reduce((acc, c) => acc + (c.unread || 0), 0));
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'No se pudieron cargar los canales.');
      })
      .finally(() => setCargando(false));
  }, [token, smackVisible]);

  useSmackPolling(cargarCanales, CHANNELS_POLL_MS, Boolean(token && smackVisible));

  // Enviar/leer en esta misma sesión refresca la lista (previews y contadores)
  // sin esperar al sondeo de 15 s.
  useEffect(() => alCambiarSmack(cargarCanales), [cargarCanales]);

  if (!smackVisible) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-gray-500">
        Smack no está habilitado en este club.
      </div>
    );
  }

  const activeChannel = channels.find((c) => c.id === activeChannelId) || null;
  // En Pendientes no hay "canal activo" pero el panel debe ocupar toda la
  // sección igual que un canal abierto (mismo criterio de apilado en móvil).
  const seccionOcupada = Boolean(activeChannel) || vista === 'pendientes';

  const abrirCanalDesdePendiente = (channelId) => {
    setVista('canales');
    setActiveChannelId(channelId);
  };

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Lista de canales: panel fijo en desktop; en móvil solo si no hay canal/pendientes abierto */}
      <aside
        className={`min-h-0 w-full flex-col border-gray-200 md:flex md:w-72 md:shrink-0 md:border-r ${
          seccionOcupada ? 'hidden' : 'flex'
        }`}
      >
        <ChannelList
          channels={channels}
          activeChannelId={activeChannelId}
          cargando={cargando}
          error={error}
          onSelect={(id) => {
            setVista('canales');
            setActiveChannelId(id);
          }}
          isSuperadmin={isSuperadmin}
          vista={vista}
          onCambiarVista={(v) => {
            setVista(v);
            if (v === 'pendientes') setActiveChannelId(null);
          }}
          pendientesCount={pendientesCount}
          onNuevoCanal={() => setCreandoCanal(true)}
          onVerVideo={() => setViendoVideo(true)}
        />
      </aside>

      {/* Canal/Pendientes abierto: en móvil ocupa todo con botón atrás */}
      <section className={`min-h-0 min-w-0 flex-1 flex-col ${seccionOcupada ? 'flex' : 'hidden md:flex'}`}>
        {vista === 'pendientes' ? (
          <PendingIssuesPanel
            token={token}
            onOpenChannel={abrirCanalDesdePendiente}
            onBack={() => setVista('canales')}
            isSuperadmin={isSuperadmin}
          />
        ) : activeChannel ? (
          <ChannelView
            key={activeChannel.id}
            channel={activeChannel}
            token={token}
            myUserId={myUserId}
            onBack={() => setActiveChannelId(null)}
            isSuperadmin={isSuperadmin}
            onVerVideo={() => setViendoVideo(true)}
            onArchivado={() => {
              setActiveChannelId(null);
              cargarCanales();
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center p-8 text-center text-sm text-gray-400">
            Elige un canal para empezar.
          </div>
        )}
      </section>

      {isSuperadmin ? (
        <CreateChannelModal
          active={creandoCanal}
          token={token}
          venues={allVenues || []}
          onCerrar={() => setCreandoCanal(false)}
          onCreado={(canal) => {
            cargarCanales();
            setVista('canales');
            setActiveChannelId(canal.id);
          }}
        />
      ) : null}

      <HelpVideoModal active={viendoVideo} onClose={() => setViendoVideo(false)} />
    </div>
  );
}
